import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MapPin } from "lucide-react";

const BUILDING_IMG =
  "https://customer-assets.emergentagent.com/job_5adba607-3cb4-4890-a176-530c0c902f8d/artifacts/kkkl20b5_imgi_1_IMG_1653-1-scaled.jpg";
const LOGO_IMG =
  "https://customer-assets.emergentagent.com/job_5adba607-3cb4-4890-a176-530c0c902f8d/artifacts/ssy7xi7j_imgi_2_Banner_Familie.png";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const yMid = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);
  const opacityLogo = useTransform(scrollYProgress, [0, 0.6], [0.85, 0]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden bg-[var(--cream)]"
      data-testid="hero-section"
    >
      {/* Parallax background — sage building */}
      <motion.div
        style={{ y: yBg }}
        className="absolute inset-0 z-0"
        aria-hidden
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${BUILDING_IMG})` }}
        />
        <div className="absolute inset-0 bg-[var(--cream)]/65" />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--cream)] via-transparent to-[var(--cream)]" />
      </motion.div>

      {/* Macro Vespa logo — middle layer (right side) */}
      <motion.div
        style={{ y: yMid, opacity: opacityLogo }}
        className="hidden md:block absolute -right-32 lg:-right-20 top-1/2 -translate-y-1/2 w-[55vw] max-w-[760px] aspect-square pointer-events-none z-10"
        aria-hidden
      >
        <div
          className="w-full h-full bg-contain bg-no-repeat bg-center mix-blend-multiply"
          style={{ backgroundImage: `url(${LOGO_IMG})` }}
        />
      </motion.div>

      {/* Foreground — typography (NO parallax to ensure render) */}
      <div className="relative z-20 container-wide pt-32 md:pt-36 lg:pt-40 pb-16 min-h-screen flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="tricolore h-[3px] w-12 shrink-0" />
          <span className="overline whitespace-nowrap text-[10px]">
            Fondata nel 1949 · Binningen
          </span>
        </motion.div>

        <h1
          className="h-display text-[clamp(2.75rem,7vw,7rem)] text-[var(--anthracite)] max-w-[14ch] relative"
          data-testid="hero-headline"
        >
          <span className="block">Legende.</span>
          <span className="block italic-accent text-[var(--sage-dark)]">Leidenschaft.</span>
          <span className="block">Lifestyle.</span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8 }}
          className="mt-10 max-w-xl text-base md:text-lg text-[var(--muted-fg)] font-light leading-relaxed"
        >
          Ihr Spezialist für{" "}
          <em className="italic-accent text-[var(--anthracite)] not-italic font-medium">
            Vespa
          </em>{" "}
          &{" "}
          <em className="italic-accent text-[var(--anthracite)] not-italic font-medium">
            Pony Mofas
          </em>{" "}
          in Basel / Binningen — seit über 75 Jahren. Wir leben den Kult, aus Leidenschaft.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-12 flex flex-wrap gap-4 items-center"
        >
          <Link to="/vespa-piaggio" className="btn-primary" data-testid="hero-cta-models">
            Modelle entdecken
            <ArrowDownRight size={16} strokeWidth={1.5} />
          </Link>
          <Link to="/casa-markus" className="btn-ghost" data-testid="hero-cta-appointment">
            Service-Termin buchen
          </Link>
        </motion.div>

        <motion.a
          href="https://maps.google.com/?q=Hauptstrasse+87+Binningen"
          target="_blank"
          rel="noreferrer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-16 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-[var(--muted-fg)] hover:text-[var(--sage-dark)] transition-colors self-start"
          data-testid="hero-address-link"
        >
          <MapPin size={14} strokeWidth={1.4} />
          Hauptstrasse 74 · 4102 Binningen
        </motion.a>
      </div>
    </section>
  );
}
